// CandidateProfileSummary — compact identity/profile card for the candidate Home
// right rail. At-a-glance: avatar + name, completeness, key prefs, edit link.
// A summary, NOT a duplicate of the profile page.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, Calendar, GraduationCap, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCandidateProfile } from '@/contexts/CandidateProfileContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { ProgressBar } from '@/components/custom-ui/score-bar';
import { candidateProfileCompletion } from '@/lib/profileCompletion';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';

const fmtDate = (iso?: string) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('sv-SE'); } catch { return iso; }
};

const Fact: React.FC<{ icon: typeof MapPin; label: string; value: string }> = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-2.5">
        <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className="text-sm text-gray-500">{label}</span>
        <span className="text-sm font-medium text-gray-900 ml-auto truncate">{value}</span>
    </div>
);

const CandidateProfileSummary: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { userProfile: p, profileDocuments } = useCandidateProfile();
    const hasCv = profileDocuments.some((d) => d.document_type === 'cv');
    const { percent, complete, missing } = candidateProfileCompletion(p, hasCv);

    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Min profil';
    const initials = ((user?.first_name?.[0] ?? '') + (user?.last_name?.[0] ?? '')).toUpperCase() || (user?.email?.[0] ?? '?').toUpperCase();
    const hours = p?.min_hours_per_week && p?.max_hours_per_week ? `${p.min_hours_per_week}–${p.max_hours_per_week} h/v` : '—';

    return (
        <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-5`}>
            <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12 ring-2 ring-white shadow-sm">
                    <AvatarImage src={resolveMediaUrl(user?.picture_url)} alt={name} />
                    <AvatarFallback className="bg-[#1A4D4F] text-[#f5f5f5] font-semibold">{initials}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{name}</p>
                    <p className="text-sm text-gray-500 truncate">{p?.location || 'Kandidat'}</p>
                </div>
            </div>

            {/* Completeness */}
            <div className="mt-4">
                {complete ? (
                    <p className="text-sm font-medium text-green-600">✓ Profil komplett</p>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-500">Profil ifylld</span>
                            <span className="text-xs font-semibold text-[#1A4D4F]">{percent}%</span>
                        </div>
                        <ProgressBar value={percent} color="teal" />
                        {/* #359 — name what is missing, and why it is worth doing. A bare
                            percentage tells someone they are incomplete without telling
                            them what to do about it, and the CV in particular is the one
                            thing an employer reads. */}
                        <p className="text-xs text-gray-500 mt-2">
                            {missing.includes('CV')
                                ? 'Ladda upp din CV — det är den arbetsgivare läser först.'
                                : `Saknas: ${missing.join(', ')}.`}
                        </p>
                    </>
                )}
            </div>

            {/* Key facts */}
            <div className="mt-4 space-y-2.5 border-t border-gray-100 pt-4">
                <Fact icon={MapPin} label="Plats" value={p?.location || '—'} />
                <Fact icon={Clock} label="Omfattning" value={hours} />
                <Fact icon={Calendar} label="Tillgänglig" value={fmtDate(p?.available_from_date)} />
                {/* #359 — the Utbildning fact is gone: the profile no longer collects it,
                    so on every new account this row was a permanent "—". Existing values
                    are still stored and still reach the employer's applicant view. */}
            </div>

            <button
                onClick={() => navigate('/dashboard/profile')}
                className="mt-4 w-full flex items-center justify-center gap-1.5 text-sm font-medium text-[#1A4D4F] hover:bg-[#A4C3B2]/20 rounded-xl py-2 transition-colors"
            >
                Redigera profil <ArrowRight className="w-4 h-4" />
            </button>
        </div>
    );
};

export default CandidateProfileSummary;
